import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useState } from "react";

import { EmailNewsletterId } from "../../../components/EmailPreferencesForm/EmailPreferencesForm.types";
import { customDebounce } from "../../../utilities/debounce";
import { keyForId } from "./utilities";
import {
  PutAccountSubApiArg,
  PutIccSubApiArg,
  PutIntegratorSubApiArg,
  PutNewsFeedSubApiArg,
  PutProductSupportSubApiArg,
  PutUserFeedbackGroupSubApiArg,
  PutWebinarSubApiArg,
  PutWorkspacesActivitySubApiArg,
  PutWorkspaceSubApiArg,
  usePutAccountSubMutation,
  usePutIccSubMutation,
  usePutIntegratorSubMutation,
  usePutNewsFeedSubMutation,
  usePutProductSupportSubMutation,
  usePutUnsubscribeAllMutation,
  usePutUserFeedbackGroupSubMutation,
  usePutWebinarSubMutation,
  usePutWorkspacesActivitySubMutation,
  usePutWorkspaceSubMutation,
} from "../api/emailPreferences.api";
import { LastRequestId } from "./EmailPreferencesAuth.types";

export const useEmailQueries = () => {
  const [lastRequest, setLastRequest] = useState<LastRequestId | undefined>();

  const [updateAccount, accountStatus] = usePutAccountSubMutation();
  const [updateNewsfeed, newsfeedStatus] = usePutNewsFeedSubMutation();
  const [updateWebinars, webinarsStatus] = usePutWebinarSubMutation();
  const [updateSupport, supportStatus] = usePutProductSupportSubMutation();
  const [updateIntegrators, integratorsStatus] = usePutIntegratorSubMutation();
  const [updateUserFeedbackGroup, feedbackStatus] =
    usePutUserFeedbackGroupSubMutation();
  const [updateConference, conferenceStatus] = usePutIccSubMutation();
  const [updateWorkspaces, workspacesStatus] = usePutWorkspaceSubMutation();
  const [updateWorkspacesActivity, workspacesActivityStatus] =
    usePutWorkspacesActivitySubMutation();

  const updateStatus = [
    accountStatus,
    newsfeedStatus,
    webinarsStatus,
    supportStatus,
    integratorsStatus,
    feedbackStatus,
    conferenceStatus,
    workspacesStatus,
    workspacesActivityStatus,
  ];

  const updateIsLoading = updateStatus.reduce<boolean>(
    (total, state) => total || state.isLoading,
    false
  );
  const updateIsError = updateStatus.reduce<boolean>(
    (total, state) => total || state.isError,
    false
  );
  const updateIsSuccess = updateStatus.reduce<boolean>(
    (total, state) => total || state.isSuccess,
    false
  );
  const updateErrors = updateStatus.reduce<
    (FetchBaseQueryError | SerializedError)[]
  >((total, state) => (state.error ? [...total, state.error] : total), []);

  const [unsubscribeAll, unsubscribeAllState] = usePutUnsubscribeAllMutation();

  const mutationForKey = {
    account: updateAccount,
    newsfeed: updateNewsfeed,
    webinars: updateWebinars,
    ignitionSupportAndProductUpdates: updateSupport,
    integrators: updateIntegrators,
    userFeedbackGroup: updateUserFeedbackGroup,
    ignitionCommunityConference: updateConference,
    workspace: updateWorkspaces,
    workspacesActivity: updateWorkspacesActivity,
  };

  const updateSubscription = async (
    id: EmailNewsletterId,
    subscribed: boolean
  ) => {
    const type = keyForId(id);
    setLastRequest(type);

    if (type) {
      const payloadName = `${type}SubPayload`;

      const payload = {
        [payloadName]: { [type]: subscribed },
      } as PutAccountSubApiArg &
        PutIccSubApiArg &
        PutIntegratorSubApiArg &
        PutNewsFeedSubApiArg &
        PutProductSupportSubApiArg &
        PutUserFeedbackGroupSubApiArg &
        PutWebinarSubApiArg &
        PutWorkspacesActivitySubApiArg &
        PutWorkspaceSubApiArg;
      await mutationForKey[type](payload);
    }
  };

  const unsubscribeFromAll = async () => {
    setLastRequest("unsubscribe_all");
    await unsubscribeAll();
  };

  return {
    updateSubscription: customDebounce(updateSubscription),
    unsubscribeAll: customDebounce(unsubscribeFromAll),
    isLoading: updateIsLoading || unsubscribeAllState.isLoading,
    isError: updateIsError || unsubscribeAllState.isError,
    isSuccess: updateIsSuccess || unsubscribeAllState.isSuccess,
    errors: [...updateErrors, unsubscribeAllState.error],
    lastRequest,
  };
};
